import React, { useEffect, useRef, useState } from 'react';
import styles from './FirstScreen.module.css';
import esther from "../images/אסתר מטטוב תמונה ראשית.png";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Popped from '../components/poppedUpWindow/Popped';
import Loader from '../components/loader/Loader';

gsap.registerPlugin(ScrollTrigger);

const HeroSection = () => {
  const preHeaderRef = useRef(null);
  const textBlockRef = useRef(null);
  const imageRef = useRef(null);
  const statsRowRef = useRef(null);
  const [isPoppedOpen, setIsPoppedOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (!imageLoaded) return;

    gsap.fromTo(
      preHeaderRef.current,
      { opacity: 0, y: -50, filter: 'blur(8px)', scale: 0.95 },
      {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        scale: 1,
        duration: 1.2,
        ease: 'back.out(1.4)',
        scrollTrigger: {
          trigger: preHeaderRef.current,
          start: 'top 95%',
        },
      }
    );

    const textElements = textBlockRef.current?.children || [];
    gsap.fromTo(
      textElements,
      { opacity: 0, y: 60, filter: 'blur(4px)', scale: 0.9 },
      {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        scale: 1,
        duration: 1.2,
        ease: 'power3.out',
        stagger: 0.2,
        scrollTrigger: {
          trigger: textBlockRef.current,
          start: 'top 85%',
        },
      }
    );

    gsap.fromTo(
      imageRef.current,
      {
        opacity: 0,
        scale: 0.8,
        rotateY: -20,
        rotateX: 10,
        filter: 'blur(6px) brightness(0.8)'
      },
      {
        opacity: 1,
        scale: 1,
        rotateY: 0,
        rotateX: 0,
        filter: 'blur(0px) brightness(1)',
        duration: 1.6,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: imageRef.current,
          start: 'top 85%',
        },
      }
    );

    gsap.fromTo(
      statsRowRef.current,
      {
        opacity: 0,
        y: 40,
        scale: 0.95,
        filter: 'blur(6px)'
      },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        filter: 'blur(0px)',
        duration: 1.4,
        ease: 'back.out(1.2)',
        scrollTrigger: {
          trigger: statsRowRef.current,
          start: 'top 90%',
        },
        delay: 0.3
      }
    );

    const statNumbers = statsRowRef.current?.querySelectorAll(`.${styles.statNumber}`);

    if (statNumbers[0]) {
      let counter1 = { value: 0 };
      statNumbers[0].textContent = '0+';
      gsap.to(counter1, {
        value: 2000,
        duration: 2,
        ease: 'power2.out',
        delay: 0.5,
        onUpdate: () => {
          statNumbers[0].textContent = Math.round(counter1.value) + '+';
        },
        scrollTrigger: {
          trigger: statsRowRef.current,
          start: 'top 90%',
        }
      });
    }

    if (statNumbers[1]) {
      let counter2 = { value: 0 };
      statNumbers[1].textContent = '0+';
      gsap.to(counter2, {
        value: 22,
        duration: 2,
        ease: 'power2.out',
        delay: 0.7,
        onUpdate: () => {
          statNumbers[1].textContent = Math.round(counter2.value) + '+';
        },
        scrollTrigger: {
          trigger: statsRowRef.current,
          start: 'top 90%',
        }
      });
    }

    if (statNumbers[2]) {
      let counter3 = { value: 0 };
      statNumbers[2].textContent = '0%';
      gsap.to(counter3, {
        value: 100,
        duration: 2,
        ease: 'power2.out',
        delay: 0.9,
        onUpdate: () => {
          statNumbers[2].textContent = Math.round(counter3.value) + '%';
        },
        scrollTrigger: {
          trigger: statsRowRef.current,
          start: 'top 90%',
        }
      });
    }

    gsap.to(imageRef.current, {
      y: -30,
      scrollTrigger: {
        trigger: imageRef.current,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1.5
      }
    });

  }, [imageLoaded]);

  const handleButtonClick = () => {
    setIsPoppedOpen(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <>
      <div className={styles.preHeader} ref={preHeaderRef}>
        אם תמיד הסתכלת על תחום היופי ורצית להצליח בו בענק - תמשיכי לקרוא הדף הזה במיוחד עבורך 
      </div>
      <section className={styles.hero}>
        <div className={styles.container}>
          <div className={styles.textContent} ref={textBlockRef}>
            <div className={styles.title}>
            הדרך שלך לקריירה בתחום הציפורניים שתוביל אותך להכנסה אמיתית <span className={styles.highlightedText}> ועבודות ברמה של חו׳׳ל </span>
            </div>

            <p className={styles.descriptionP}>
              בקורסים של אסתר מטי תלמדי שיטה ייחודית באקריל ובג׳ל, תתרגלי כבר במהלך הקורס ותצאי מוכנה לעבוד – עם ביטחון וידע שמאפשרים לך לגבות מחירים גבוהים מהרגיל ולהתפרנס ממה שאת אוהבת.
            </p>

            <div className={styles.glassContainer}>
              <p className={styles.description}>
                הקורסים מתאימים גם למתחילות וגם למי שכבר בתחום ורוצות לשדרג את הרמה. תוכלי ללמוד בפרטני או בקבוצות קטנות, עם יחס אישי והדרכה צמודה, עד שתדעי לבנות לכל לקוחה, כל ציפורן ובכל גודל בדיוק מושלם (xs-5xl).
              </p>
            </div>

            <button onClick={handleButtonClick} className={styles.ctaButton}>
              אני רוצה ללמוד לבנות ציפורניים כמו מקצוענית
            </button>
          </div>

          <div className={styles.imageWrapper} ref={imageRef}>
            {!imageLoaded && <Loader />}
            
            <img
              src={esther}
              alt="אסתר מטטוב"
              className={styles.image}
              onLoad={handleImageLoad}
              style={{ opacity: imageLoaded ? 1 : 0 }}
            />
          </div>
        </div>
      </section>

      <div className={styles.statsRow} ref={statsRowRef}>
        <div className={styles.statsContainer}>
          <div className={styles.statItem}>
            <div className={styles.statNumber}>2000+</div>
            <div className={styles.statLabel}>לקוחות</div>
          </div>
          
          <div className={styles.statDivider}></div>
          
          <div className={styles.statItem}>
            <div className={styles.statNumber}>22+</div>
            <div className={styles.statLabel}>שנות ניסיון</div>
          </div>
          
          <div className={styles.statDivider}></div>
          
          <div className={styles.statItem}>
            <div className={styles.statNumber}>100%</div>
            <div className={styles.statLabel}>תרגול מעשי</div>
          </div>
        </div>
      </div>
      
      {isPoppedOpen && <Popped onClose={() => setIsPoppedOpen(false)} />}
    </>
  );
};

export default HeroSection;